import axios from 'axios'

/** Which capture point produced the lead — mirrors the backend `source`. */
type LeadSource =
  | 'contact_form'
  | 'book_visit'
  | 'contact_box'
  | 'request_offer'
  | 'promo_popup'

/**
 * Additive contact-form contract: `text` stays the required, already-polished
 * Telegram body every form builds today, and the structural fields ride
 * alongside it so the backend can persist a lead without regexing a phone out
 * of the blob.
 */
export type ContactFormLead = {
  text: string
  source: LeadSource
  name?: string
  phone?: string
  email?: string
  message?: string
}

type ContactFormBody = Record<string, string>

const buildBody = (lead: ContactFormLead): ContactFormBody => {
  const { text, source, ...optional } = lead
  const body: ContactFormBody = {
    text,
    source,
    pageUrl: window.location.href,
  }
  for (const [key, value] of Object.entries(optional)) {
    const trimmed = value?.trim()
    if (trimmed) body[key] = trimmed
  }
  return body
}

/**
 * The backend answers `{ success: true }` after the lead is committed. Anything
 * else — a failed request, or a 200 whose body says nothing — means we do not
 * know that the lead exists, and the caller must not claim it does.
 */
const assertAccepted = (data: unknown): void => {
  if ((data as { success?: unknown } | undefined)?.success !== true) {
    throw new Error('contact-form answered without a success flag')
  }
}

export async function submitContactForm(lead: ContactFormLead): Promise<void> {
  const response = await axios.post('/api/contact-form', buildBody(lead))
  assertAccepted(response.data)
}

export async function submitContactFormWithPhotos(
  lead: ContactFormLead,
  photos: File[]
): Promise<void> {
  const form = new FormData()
  for (const [key, value] of Object.entries(buildBody(lead))) {
    form.append(key, value)
  }
  photos.forEach((file) => form.append('photos', file))

  const response = await axios.post('/api/contact-form-photos', form)
  assertAccepted(response.data)
}
